import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiFileText,
  FiFolder,
  FiPlus,
  FiRefreshCw,
  FiX
} from "react-icons/fi";
import BuildDocumentCard from "./BuildDocumentCard";
import DocumentViewerModal from "./DocumentViewerModal";
import UploadBuildDocumentModal from "./UploadBuildDocumentModal";
import {
  BUILD_DOCUMENT_CATEGORIES,
  fetchBuildDocuments,
  deleteBuildDocument
} from "../utils/buildDocuments";

function BuildBinder({
  ownerId,
  username,
  carId,
  isOwner,
  initialUploadOpen = false,
  maintenanceItems = [],
  mods = []
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [uploadModalOpen, setUploadModalOpen] = useState(Boolean(initialUploadOpen));
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [viewDocument, setViewDocument] = useState(null);

  useEffect(() => {
    if (initialUploadOpen) {
      setUploadModalOpen(true);
    }
  }, [initialUploadOpen]);

  const loadDocuments = useCallback(async () => {
    if (!isOwner || !ownerId || !carId) return;

    setLoading(true);
    setError("");

    try {
      const loadedDocuments = await fetchBuildDocuments({ ownerId, carId });
      setDocuments(Array.isArray(loadedDocuments) ? loadedDocuments : []);
    } catch (loadError) {
      console.log("Build Binder load failed:", loadError);
      setError(
        loadError?.message ||
          "We could not load your Build Binder documents. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [isOwner, ownerId, carId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const categoryCounts = useMemo(() => {
    const counts = {
      All: documents.length
    };

    BUILD_DOCUMENT_CATEGORIES.forEach((category) => {
      counts[category] = documents.filter(
        (documentItem) => documentItem.category === category
      ).length;
    });

    return counts;
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    if (selectedCategory === "All") return documents;

    return documents.filter(
      (documentItem) => documentItem.category === selectedCategory
    );
  }, [documents, selectedCategory]);

  const handleUploaded = (newDocument) => {
    if (!newDocument?.id) return;

    setDocuments((currentDocuments) => {
      const alreadyExists = currentDocuments.some(
        (documentItem) => documentItem.id === newDocument.id
      );

      if (alreadyExists) return currentDocuments;

      return [newDocument, ...currentDocuments];
    });
  };

  const handleViewDocument = (documentItem) => {
    if (!documentItem || !documentItem.downloadUrl) return;

    setViewDocument(documentItem);
  };

  const handleCloseViewer = () => {
    setViewDocument(null);
  };

  const handleDeleteRequest = (documentItem) => {
    if (!documentItem || deletingDocumentId) return;

    setViewDocument(null);
    setDeleteCandidate(documentItem);
    setDeleteError("");
  };

  const handleCancelDelete = () => {
    if (deletingDocumentId) return;

    setDeleteCandidate(null);
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate || deletingDocumentId) return;

    const documentIdToDelete = deleteCandidate.id;

    setDeletingDocumentId(documentIdToDelete);
    setDeleteError("");

    try {
      await deleteBuildDocument(deleteCandidate);

      setDocuments((currentDocuments) =>
        currentDocuments.filter(
          (documentItem) => documentItem.id !== documentIdToDelete
        )
      );

      setViewDocument((currentViewDocument) =>
        currentViewDocument?.id === documentIdToDelete ? null : currentViewDocument
      );

      setDeleteCandidate(null);
    } catch (deleteErrorValue) {
      console.log("Build Binder delete failed:", deleteErrorValue);
      setDeleteError(
        deleteErrorValue?.message ||
          "We could not delete this document. Please try again."
      );
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const handleOverlayMouseDown = (event) => {
    if (event.target === event.currentTarget && !deletingDocumentId) {
      handleCancelDelete();
    }
  };

  if (!isOwner) {
    return null;
  }

  const hasDocuments = documents.length > 0;
  const isInitialLoading = loading && !hasDocuments;
  const selectedCategoryIsEmpty = hasDocuments && filteredDocuments.length === 0;

  return (
    <section style={binderStyle} aria-labelledby="build-binder-title">
      <div style={headerStyle}>
        <div style={headerCopyStyle}>
          <div style={eyebrowRowStyle}>
            <FiFolder aria-hidden="true" />
            <span>Private Records</span>
          </div>

          <h2 id="build-binder-title" style={titleStyle}>
            Build Binder
          </h2>

          <p style={subtitleStyle}>
            Receipts, warranties, invoices, and private vehicle records.
          </p>

          <div style={statsRowStyle}>
            <span style={statPillStyle}>
              <FiFileText aria-hidden="true" />
              {documents.length} {documents.length === 1 ? "Document" : "Documents"}
            </span>
          </div>
        </div>

        <div style={headerActionsStyle}>
          <button
            type="button"
            onClick={() => setUploadModalOpen(true)}
            style={primaryButtonStyle}
          >
            <FiPlus aria-hidden="true" />
            Add Document
          </button>

          <button
            type="button"
            onClick={loadDocuments}
            disabled={loading}
            aria-label="Refresh Build Binder documents"
            style={{
              ...iconButtonStyle,
              opacity: loading ? 0.65 : 1,
              cursor: loading ? "not-allowed" : "pointer"
            }}
          >
            <FiRefreshCw aria-hidden="true" />
          </button>
        </div>
      </div>

      <div style={filterRowStyle} aria-label="Build Binder category filters">
        {["All", ...BUILD_DOCUMENT_CATEGORIES].map((category) => {
          const isSelected = selectedCategory === category;

          return (
            <button
              key={category}
              type="button"
              onClick={() => setSelectedCategory(category)}
              style={{
                ...filterButtonStyle,
                ...(isSelected ? activeFilterButtonStyle : {})
              }}
            >
              <span>{category}</span>
              <span
                style={{
                  ...filterCountStyle,
                  ...(isSelected ? activeFilterCountStyle : {})
                }}
              >
                {categoryCounts[category] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div style={errorBoxStyle} role="alert">
          <p style={errorTextStyle}>{error}</p>

          <button
            type="button"
            onClick={loadDocuments}
            disabled={loading}
            style={retryButtonStyle}
          >
            Retry
          </button>
        </div>
      )}

      {isInitialLoading ? (
        <div style={loadingStateStyle}>
          <FiRefreshCw aria-hidden="true" />
          <p style={loadingTextStyle}>Loading...</p>
        </div>
      ) : (
        <>
          {!hasDocuments && (
            <div style={emptyStateStyle}>
              <div style={emptyIconStyle}>
                <FiFolder aria-hidden="true" />
              </div>

              <h3 style={emptyTitleStyle}>No documents yet.</h3>

              <p style={emptyTextStyle}>
                Upload your first receipt, warranty, invoice, or vehicle record.
              </p>

              <button
                type="button"
                onClick={() => setUploadModalOpen(true)}
                style={secondaryButtonStyle}
              >
                <FiPlus aria-hidden="true" />
                Upload Document
              </button>
            </div>
          )}

          {selectedCategoryIsEmpty && (
            <div style={emptyStateStyle}>
              <div style={emptyIconStyle}>
                <FiFileText aria-hidden="true" />
              </div>

              <h3 style={emptyTitleStyle}>No documents in this category.</h3>

              <button
                type="button"
                onClick={() => setSelectedCategory("All")}
                style={secondaryButtonStyle}
              >
                View All Documents
              </button>
            </div>
          )}

          {filteredDocuments.length > 0 && (
            <div style={documentListStyle}>
              {filteredDocuments.map((documentItem) => (
                <BuildDocumentCard
                  key={documentItem.id}
                  documentItem={documentItem}
                  onView={handleViewDocument}
                  onDelete={handleDeleteRequest}
                  deleting={deletingDocumentId === documentItem.id}
                />
              ))}
            </div>
          )}
        </>
      )}

      <UploadBuildDocumentModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        ownerId={ownerId}
        username={username}
        carId={carId}
        maintenanceItems={maintenanceItems}
        mods={mods}
        onUploaded={handleUploaded}
      />

      <DocumentViewerModal
        isOpen={Boolean(viewDocument)}
        onClose={handleCloseViewer}
        documentItem={viewDocument}
      />

      {deleteCandidate && (
        <div style={modalOverlayStyle} onMouseDown={handleOverlayMouseDown}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-document-title"
            style={deleteModalStyle}
          >
            <div style={deleteModalHeaderStyle}>
              <h3 id="delete-document-title" style={deleteModalTitleStyle}>
                Delete document?
              </h3>

              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={Boolean(deletingDocumentId)}
                aria-label="Close delete confirmation"
                style={{
                  ...closeButtonStyle,
                  opacity: deletingDocumentId ? 0.5 : 1,
                  cursor: deletingDocumentId ? "not-allowed" : "pointer"
                }}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <p style={deleteModalTextStyle}>
              This will permanently remove this file from the Build Binder. This
              action cannot be undone.
            </p>

            <div style={deleteDocumentBoxStyle}>
              <p style={deleteDocumentLabelStyle}>Document</p>
              <p style={deleteDocumentTitleStyle}>
                {deleteCandidate.title || "Untitled document"}
              </p>
            </div>

            {deleteError && (
              <div style={deleteErrorBoxStyle} role="alert">
                {deleteError}
              </div>
            )}

            <div style={deleteActionsStyle}>
              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={Boolean(deletingDocumentId)}
                style={{
                  ...secondaryButtonStyle,
                  opacity: deletingDocumentId ? 0.65 : 1,
                  cursor: deletingDocumentId ? "not-allowed" : "pointer"
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={Boolean(deletingDocumentId)}
                style={{
                  ...dangerButtonStyle,
                  opacity: deletingDocumentId ? 0.7 : 1,
                  cursor: deletingDocumentId ? "not-allowed" : "pointer"
                }}
              >
                {deletingDocumentId ? "Deleting..." : "Delete Document"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const binderStyle = {
  width: "100%",
  boxSizing: "border-box",
  color: "white"
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "18px",
  flexWrap: "wrap",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "22px",
  padding: "22px",
  boxSizing: "border-box",
  marginBottom: "18px"
};

const headerCopyStyle = {
  flex: "1 1 280px",
  minWidth: 0
};

const eyebrowRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#e10600",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  marginBottom: "10px"
};

const titleStyle = {
  margin: 0,
  fontSize: "28px",
  lineHeight: "1.1",
  fontWeight: "800",
  color: "#fff"
};

const subtitleStyle = {
  margin: "12px 0 0",
  color: "#b8b8b8",
  fontSize: "15px",
  lineHeight: "1.55",
  maxWidth: "680px"
};

const statsRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginTop: "16px"
};

const statPillStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#111",
  border: "1px solid #303030",
  borderRadius: "999px",
  padding: "8px 12px",
  color: "#dedede",
  fontSize: "13px",
  fontWeight: "700"
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
  flexWrap: "wrap",
  flex: "0 1 auto"
};

const primaryButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "44px",
  border: "none",
  borderRadius: "14px",
  padding: "12px 16px",
  backgroundColor: "#e10600",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "800",
  cursor: "pointer",
  boxSizing: "border-box",
  transition: "transform 160ms ease, background-color 160ms ease"
};

const secondaryButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "44px",
  border: "1px solid #3a3a3a",
  borderRadius: "14px",
  padding: "12px 16px",
  backgroundColor: "#242424",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "800",
  cursor: "pointer",
  boxSizing: "border-box"
};

const iconButtonStyle = {
  width: "44px",
  height: "44px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #3a3a3a",
  borderRadius: "14px",
  backgroundColor: "#222",
  color: "#fff",
  fontSize: "18px",
  boxSizing: "border-box"
};

const filterRowStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "18px",
  overflowX: "auto",
  maxWidth: "100%",
  boxSizing: "border-box"
};

const filterButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "40px",
  border: "1px solid #303030",
  borderRadius: "999px",
  padding: "9px 12px",
  backgroundColor: "#181818",
  color: "#d8d8d8",
  fontSize: "14px",
  fontWeight: "800",
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxSizing: "border-box"
};

const activeFilterButtonStyle = {
  borderColor: "#e10600",
  backgroundColor: "rgba(225, 6, 0, 0.14)",
  color: "#fff"
};

const filterCountStyle = {
  minWidth: "24px",
  borderRadius: "999px",
  padding: "2px 7px",
  backgroundColor: "#2a2a2a",
  color: "#bdbdbd",
  fontSize: "12px",
  fontWeight: "900",
  textAlign: "center",
  boxSizing: "border-box"
};

const activeFilterCountStyle = {
  backgroundColor: "#e10600",
  color: "#fff"
};

const errorBoxStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
  backgroundColor: "rgba(225, 6, 0, 0.12)",
  border: "1px solid rgba(225, 6, 0, 0.45)",
  borderRadius: "16px",
  padding: "14px",
  marginBottom: "18px",
  boxSizing: "border-box"
};

const errorTextStyle = {
  margin: 0,
  color: "#ffd7d7",
  fontSize: "14px",
  lineHeight: "1.5",
  flex: "1 1 240px"
};

const retryButtonStyle = {
  minHeight: "40px",
  border: "1px solid rgba(255,255,255,0.16)",
  borderRadius: "12px",
  padding: "10px 14px",
  backgroundColor: "#2a2a2a",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "800",
  cursor: "pointer"
};

const loadingStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  minHeight: "220px",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "20px",
  color: "#d8d8d8",
  boxSizing: "border-box"
};

const loadingTextStyle = {
  margin: 0,
  color: "#b8b8b8",
  fontSize: "15px",
  fontWeight: "700"
};

const emptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: "14px",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "22px",
  padding: "34px 20px",
  boxSizing: "border-box"
};

const emptyIconStyle = {
  width: "54px",
  height: "54px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "18px",
  backgroundColor: "rgba(225, 6, 0, 0.13)",
  color: "#e10600",
  fontSize: "24px"
};

const emptyTitleStyle = {
  margin: 0,
  color: "#fff",
  fontSize: "22px",
  lineHeight: "1.2",
  fontWeight: "850"
};

const emptyTextStyle = {
  margin: 0,
  color: "#b8b8b8",
  fontSize: "15px",
  lineHeight: "1.55",
  maxWidth: "520px"
};

const documentListStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  width: "100%",
  boxSizing: "border-box"
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 40000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0,0,0,0.82)",
  padding: "20px",
  boxSizing: "border-box"
};

const deleteModalStyle = {
  width: "100%",
  maxWidth: "460px",
  maxHeight: "calc(100vh - 40px)",
  overflowY: "auto",
  backgroundColor: "#181818",
  border: "1px solid #333",
  borderRadius: "22px",
  padding: "20px",
  boxSizing: "border-box",
  boxShadow: "0 24px 70px rgba(0,0,0,0.5)"
};

const deleteModalHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "10px"
};

const deleteModalTitleStyle = {
  margin: 0,
  color: "#fff",
  fontSize: "22px",
  lineHeight: "1.2",
  fontWeight: "850"
};

const closeButtonStyle = {
  width: "40px",
  height: "40px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #333",
  borderRadius: "12px",
  backgroundColor: "#222",
  color: "#fff",
  fontSize: "18px"
};

const deleteModalTextStyle = {
  margin: "0 0 16px",
  color: "#b8b8b8",
  fontSize: "15px",
  lineHeight: "1.55"
};

const deleteDocumentBoxStyle = {
  backgroundColor: "#111",
  border: "1px solid #303030",
  borderRadius: "16px",
  padding: "14px",
  marginBottom: "14px",
  boxSizing: "border-box"
};

const deleteDocumentLabelStyle = {
  margin: "0 0 5px",
  color: "#888",
  fontSize: "12px",
  fontWeight: "800",
  letterSpacing: "0.7px",
  textTransform: "uppercase"
};

const deleteDocumentTitleStyle = {
  margin: 0,
  color: "#fff",
  fontSize: "15px",
  fontWeight: "800",
  lineHeight: "1.4",
  overflowWrap: "anywhere"
};

const deleteErrorBoxStyle = {
  backgroundColor: "rgba(225, 6, 0, 0.12)",
  border: "1px solid rgba(225, 6, 0, 0.45)",
  borderRadius: "14px",
  padding: "12px",
  color: "#ffd7d7",
  fontSize: "14px",
  lineHeight: "1.45",
  marginBottom: "14px",
  boxSizing: "border-box"
};

const deleteActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  flexWrap: "wrap"
};

const dangerButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "44px",
  border: "none",
  borderRadius: "14px",
  padding: "12px 16px",
  backgroundColor: "#e10600",
  color: "#fff",
  fontSize: "15px",
  fontWeight: "850",
  cursor: "pointer",
  boxSizing: "border-box"
};

export default BuildBinder;